import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./initiative-search-results.scss";
import { getProgressColor } from "../../../../features/company-dashboard/company-dashboard-utils";
import { routes } from "../../../../constants/routes";
import linkBreakIcon from "../../../../assets/icons/link.svg";
import deleteRedIcon from "../../../../assets/icons/delete-red.svg";
import MODULES from "../../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermissions";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { deleteInitiativeById } from "../../../../api/initiative";
import DeleteConfirmationPopup from "../../delete-confirmation-popup/delete-confirmation-popup";

interface Initiative {
  initiativeId: string;
  initiativeTitle: string;
  objectiveName: string;
  progress: number;
  status: string;
  objectiveId: number;
  keyResultInitiativeMapId: number;
  keyResultsCount: number;
  objectivesCount: number;
}

interface Props {
  results: Initiative[];
  refreshData?: () => void;
}

const InitiativesSearchResults: React.FC<Props> = ({
  results,
  refreshData,
}) => {
  const { canEdit: canEditInitiatives, canDelete: canDeleteInitiatives } =
    usePermissions(MODULES.INITIATIVES);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [deletePopupId, setDeletePopupId] = useState<any>(null);

  const navigate = useNavigate();
  if (results.length === 0) {
    return <div className="no-results">No initiatives found.</div>;
  }

  const handleReLink = (initiativeId: any) => {
    const queryParams = new URLSearchParams();
    queryParams.set("dashboardTab", "Initiatives");
    queryParams.set("initiativeId", String(initiativeId));
    navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
  };
  const handleDelete = async (initiativeId: any) => {
    if (!initiativeId) return;
    try {
      await deleteInitiativeById(initiativeId);
      setIsDeletePopupOpen(false);
      setDeletePopupId(null);
      if (refreshData) {
        refreshData();
      }
    } catch {
      return;
    }
  };

  const handleRemove = (initiativeId: any) => {
    setDeletePopupId(initiativeId);
    handleDelete(initiativeId);
  };

  const userInfo = JSON.parse(localStorage.getItem("userDetails") || "{}");

  return (
    <OverlayScrollbarsComponent
      className="initiatives-results scrollable"
      options={{
        scrollbars: {
          theme: "os-theme-dark",
          autoHide: "scroll",
          autoHideDelay: 400,
        },
      }}
    >
      {results.map((initiative: any) => {
        const { mainColor, lightColor } = getProgressColor(initiative.status);
        const isShowOwnerIcon =
          initiative?.owners?.find(
            (owner: any) => owner?.ownerId === userInfo?.employeeId,
          ) || userInfo?.roles?.find((role: any) => role?.name === "Admin");

        return (
          <Box
            key={initiative.initiativeId}
            className="initiative-card"
            style={{
              cursor: initiative?.keyResultsCount > 0 ? "pointer" : "default",
              opacity: initiative?.isApproved === false ? 0.5 : 1,
            }}
            onClick={() => {
              if (initiative?.keyResultsCount > 0) {
                const queryParams = new URLSearchParams();
                queryParams.set("dashboardTab", "Initiatives");
                queryParams.set(
                  "initiativeId",
                  String(initiative.initiativeId),
                );
                navigate(
                  `${routes.COMPANYDASHBOARD}?${queryParams.toString()}`,
                );
              }
              //   })
              // );
            }}
          >
            {initiative?.isApproved === false ? (
              <div className="under-review-badge">
                <span>Under Review</span>
                <br />
              </div>
            ) : null}
            <Box className="initiative-card-inner">
              <Box sx={{ width: "calc(100% - 60px)" }}>
                <Typography
                  variant="subtitle1"
                  className="initiative-title"
                  sx={{
                    opacity: initiative?.keyResultsCount === 0 ? 0.4 : "1",
                  }}
                >
                  {initiative.initiativeTitle}
                </Typography>
                <Box className="key-results-meta">
                  <Typography
                    variant="body2"
                    className="initiative-objective"
                    style={{
                      display: "flex",
                      gap: "9px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                    sx={{
                      opacity: initiative?.keyResultsCount === 0 ? 0.4 : "1",
                    }}
                  >
                    <span className="objective-label">Objectives</span>{" "}
                    {initiative.objectivesCount}
                    <Divider orientation="vertical" flexItem />
                    <span className="objective-label">Key Results</span>{" "}
                    {initiative.keyResultsCount}
                  </Typography>
                  {initiative?.keyResultsCount === 0 && isShowOwnerIcon && (
                    <div>
                      <Tooltip title={"Link item to reactivate"}>
                        {canEditInitiatives ? (
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              marginRight: "8px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleReLink(initiative.initiativeId)
                            }
                            type="button"
                          >
                            <img
                              alt="link Break"
                              src={linkBreakIcon}
                              className="link-icon"
                            />
                          </button>
                        ) : (
                          <></>
                        )}
                      </Tooltip>
                      <Tooltip title={"Remove Item"}>
                        {canDeleteInitiatives ? (
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleRemove(initiative.initiativeId)
                            }
                            type="button"
                          >
                            <img
                              alt="Delete icon"
                              src={deleteRedIcon}
                              className="close-icon"
                            />
                          </button>
                        ) : (
                          <></>
                        )}
                      </Tooltip>
                    </div>
                  )}
                </Box>
              </Box>

              {/* Right Section (Progress Circle) */}
              <Box
                className="initiative-progress"
                sx={{ opacity: initiative?.keyResultsCount === 0 ? 0.4 : "1" }}
              >
                {/* Background Track */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={60}
                  thickness={5}
                  sx={{
                    color: lightColor,
                    position: "absolute",
                  }}
                />
                {/* Actual Progress */}
                <CircularProgress
                  variant="determinate"
                  value={initiative.progress}
                  size={60}
                  thickness={5}
                  sx={{
                    color: mainColor,
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
                {/* Label */}
                <Box className="progress-label">
                  {Math.round(initiative.progress || 0)}%
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
      {isDeletePopupOpen && (
        <DeleteConfirmationPopup
          isOpen={true}
          onClose={() => {
            setIsDeletePopupOpen(false);
            setDeletePopupId(null);
          }}
          handleDelete={handleDelete}
          deleteId={deletePopupId}
        />
      )}
    </OverlayScrollbarsComponent>
  );
};

export default InitiativesSearchResults;
